import { useEffect, useMemo } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router';
import {
    LayoutGrid01,
    Package,
    Users01,
    BookOpen01,
    Settings01,
    HelpCircle,
    Bell01,
    Calendar,
    ShoppingCart01,
    Globe02
} from '@untitledui/icons';
import { SidebarNavigationSimple } from '@/components/application/app-navigation/sidebar-navigation/sidebar-simple';
import { AICTACard } from '@/components/application/ai-chat/ai-cta-card';
import type { NavItemType } from '@/components/application/app-navigation/config';
import { useTranslation } from 'react-i18next';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { MenuRouteGuard } from '@/components/application/route-guard/menu-route-guard';
import { useActivityUnreadCount } from '@/hooks/api/use-activities';
import { useChatAI } from '@/components/application/ai-chat/chat-ai-context';
import { AskEveFab } from '@/components/application/ai-chat/ask-eve-fab';
import { AskEveSidePanel } from '@/components/application/ai-chat/ask-eve-side-panel';

// Estendi NavItemType per includere key
type NavItemWithKey = NavItemType & { key?: string };

export const ReceiverLayout = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname;
    const { t } = useTranslation('navigation');
    const { isMenuItemEnabled } = useMenuConfiguration();
    const { openAskEvePanel, closeAskEvePanel, isAskEvePanelOpen } = useChatAI();
    const askEveEnabled = isMenuItemEnabled('ask-eve');

    // Fetch unread count for activity badge
    const { data: unreadCount = 0 } = useActivityUnreadCount('receiver');

    // Navigation items for sidebar (con chiavi per il filtraggio)
    const allSidebarItems: NavItemWithKey[] = useMemo(() => [
        {
            key: 'controlHub',
            label: t('controlHub'),
            href: '/receiver/control-hub',
            icon: LayoutGrid01
        },
        {
            key: 'calendar',
            label: t('calendar'),
            href: '/receiver/calendar',
            icon: Calendar
        },
        {
            key: 'shippers',
            label: t('shippers'),
            href: '/receiver/shippers',
            icon: Users01
        },
        {
            key: 'containers',
            label: t('containers'),
            href: '/receiver/containers',
            icon: Package
        },
        {
            key: 'liveTracking',
            label: t('liveTracking'),
            href: '/receiver/live-tracking',
            icon: Globe02
        },
        {
            key: 'orders',
            label: t('orders'),
            href: '/receiver/orders',
            icon: ShoppingCart01
        },
        {
            key: 'activity',
            label: t('activity'),
            href: '/receiver/activity',
            icon: Bell01,
            badge: unreadCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 items-center justify-center rounded-full bg-error-600 ring-1 ring-primary">
                    <span className="sr-only">{t('unreadNotifications', { count: unreadCount })}</span>
                </span>
            ) : undefined
        },
        {
            key: 'addressBook',
            label: t('addressBook'),
            href: '/receiver/address-book',
            icon: BookOpen01
        },
        {
            key: 'settings',
            label: t('settings'),
            href: '/receiver/profile',
            icon: Settings01
        }
    ], [t, unreadCount]);

    // Filtra i sidebar items in base alla configurazione menu
    const sidebarItems = useMemo(() =>
        allSidebarItems.filter(item =>
            !item.key || isMenuItemEnabled(item.key)
        ),
        [allSidebarItems, isMenuItemEnabled]
    );

    // Footer items for sidebar
    const footerItems: NavItemType[] = [
        {
            label: t('support'),
            href: '#',
            icon: HelpCircle
        },
    ];

    const env = import.meta.env.VITE_ENV;
    const hasEnvironmentBanner = env !== "production";

    const isOnFullWidthContentPage =
        currentPath === '/receiver/control-hub' ||
        currentPath === '/receiver/address-book' ||
        currentPath.startsWith('/receiver/address-book/') ||
        currentPath === '/receiver/profile' ||
        currentPath.startsWith('/receiver/profile/');

    useEffect(() => {
        if (!askEveEnabled && isAskEvePanelOpen) {
            closeAskEvePanel();
        }
    }, [askEveEnabled, isAskEvePanelOpen, closeAskEvePanel]);

    useEffect(() => {
        const st = location.state as { openAskEve?: boolean } | null;
        if (st?.openAskEve) {
            openAskEvePanel();
            navigate(
                { pathname: location.pathname, search: location.search, hash: location.hash },
                { replace: true, state: {} }
            );
        }
    }, [location.pathname, location.state, location.search, location.hash, navigate, openAskEvePanel]);

    return (
        <div className={`flex h-screen min-w-0 overflow-x-hidden bg-secondary ${hasEnvironmentBanner ? "pt-7" : ""}`}>
            <SidebarNavigationSimple
                activeUrl={currentPath}
                items={sidebarItems}
                footerItems={footerItems}
                featureCard={!isAskEvePanelOpen ? <AICTACard /> : undefined}
                showAccountCard={true}
                className="hidden lg:flex"
            />

            <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                <div className="relative flex min-h-0 min-w-0 flex-1">
                    <main className="relative z-0 min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden bg-secondary">
                        <div className={isOnFullWidthContentPage ? "mx-auto w-full min-w-0 max-w-none px-3 py-6 sm:px-4 lg:px-5" : "mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"}>
                            <MenuRouteGuard>
                                <Outlet />
                            </MenuRouteGuard>
                        </div>
                    </main>
                    {askEveEnabled && isAskEvePanelOpen ? (
                        <AskEveSidePanel variant="receiver" onClose={closeAskEvePanel} />
                    ) : null}
                    {askEveEnabled ? <AskEveFab /> : null}
                </div>
            </div>
        </div>
    );
};
